import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsApi, CRUDPermission } from '../api/permissionsApi';
import { useAuthStore } from '../store/authSlice';

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export function usePermissions() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: permissions = {} as Record<string, CRUDPermission[]>, isLoading } = useQuery({
    queryKey: ['ui-permissions', user?.id],
    queryFn: () => user?.id ? permissionsApi.getUserPermissions(user.id) : Promise.resolve({} as Record<string, CRUDPermission[]>),
    enabled: !!user?.id,
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
  });

  // Check permission with hierarchical fallback and wildcards
  const can = (path: string, operation: CRUDPermission): boolean => {
    if (!path) return true;
    
    // Check exact path
    if (permissions[path]?.includes(operation)) return true;
    
    // Check wildcard at same level (e.g., realtor.admin.*)
    const parts = path.split('.');
    if (parts.length > 1) {
      const wildcardPath = parts.slice(0, -1).join('.') + '.*';
      if (permissions[wildcardPath]?.includes(operation)) return true;
    }
    
    // Check parent paths
    while (parts.length > 1) {
      parts.pop();
      const parentPath = parts.join('.');
      
      // Check parent wildcard
      if (permissions[parentPath + '.*']?.includes(operation)) return true;
      
      // Check parent exact
      if (permissions[parentPath]?.includes(operation)) return true;
    }
    
    // Check global wildcards
    if (permissions['*.*']?.includes(operation)) return true;
    if (permissions['*']?.includes(operation)) return true;
    
    return false;
  };

  const canRead = (path: string) => can(path, 'read');
  const canCreate = (path: string) => can(path, 'create');
  const canUpdate = (path: string) => can(path, 'update');
  const canDelete = (path: string) => can(path, 'delete');

  // Get permissions for a specific path (for display in badge)
  const getPathPermissions = (path: string): CRUDPermission[] => {
    const result: CRUDPermission[] = [];
    if (canRead(path)) result.push('read');
    if (canCreate(path)) result.push('create');
    if (canUpdate(path)) result.push('update');
    if (canDelete(path)) result.push('delete');
    return result;
  };

  // Mutation for updating group permissions
  const updateMutation = useMutation({
    mutationFn: ({ groupId, permissions }: { groupId: string; permissions: Record<string, CRUDPermission[]> }) =>
      permissionsApi.setGroupPermissions(groupId, permissions),
    onSuccess: () => {
      // Invalidate user permissions cache
      queryClient.invalidateQueries({ queryKey: ['ui-permissions'] });
    },
  });

  const updateGroupPermissions = async (
    groupId: string,
    path: string,
    newPermissions: CRUDPermission[]
  ) => {
    // Get current group permissions
    const currentPerms = await permissionsApi.getGroupPermissions(groupId);
    
    // Update the specific path
    const updated = { ...currentPerms, [path]: newPermissions };
    
    // Remove empty arrays
    if (newPermissions.length === 0) {
      delete updated[path];
    }
    
    await updateMutation.mutateAsync({ groupId, permissions: updated });
  };

  const refreshPermissions = () => {
    queryClient.invalidateQueries({ queryKey: ['ui-permissions'] });
  };

  return {
    permissions,
    can,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    getPathPermissions,
    updateGroupPermissions,
    refreshPermissions,
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}

export default usePermissions;
